/**
 * Movement validation engine for D&D 5e tactical movement rules.
 * Validates movement costs, terrain effects, and grapple constraints.
 *
 * D&D 5e Rule Reference: Movement and Position (PHB p.190-192)
 * date_of_change: 2025-09-19
 */

// date_of_change: 2025-10-18 - Use shared CanonicalSchemas single source of truth
import { SpeedModes } from '../../../shared/types/CanonicalSchemas';

import { ConditionHandler, ConditionEffect } from '../handlers/ConditionHandler';

export interface Point {
  x: number;
  y: number;
}

export interface MovementPath {
  points: Point[];
  totalDistance: number;
}

export interface TerrainData {
  type: 'normal' | 'difficult' | 'hazardous' | 'impassable';
  cost: number; // Movement cost multiplier
  effects?: string[]; // Additional effects like damage
}

export interface MovementOptions {
  forcedMovement?: boolean; // Skips OA triggers if true
  ignoreActionEconomy?: boolean; // GM capability: bypasses only the normal movement budget
  grappling?: string; // ID of token being grappled/dragged
  flying?: boolean; // Uses fly speed if available
  swimming?: boolean; // Uses swim speed if available
  climbing?: boolean; // Uses climb speed if available
}

export interface MovementResult {
  ok: boolean;
  spent: number; // Movement spent
  remaining: number; // Movement remaining
  path: Point[]; // Validated path
  triggers: {
    opportunityAttacks: string[]; // Token IDs that can make OAs
  };
  errors?: string[];
  warnings?: string[];
}

export interface Battlefield {
  gridSize?: number; // Feet per grid square (default 5)
  tokens: Array<{
    id: string;
    position: Point;
    size?: number; // Token size in grid squares
    reach?: number; // Reach in feet
    conditions?: string[];
    combatFlags?: {
      disengagedThisTurn?: boolean;
    };
    activeConditionEffects?: ConditionEffect[]; // Active condition effects with durations
  }>;
  terrain?: Map<string, TerrainData>; // Position key -> terrain data
}

export class MovementEngine {
  /**
   * Validates a movement path and calculates costs.
   * @param moverId - ID of the token moving
   * @param path - Polyline path to validate
   * @param battlefield - Current battlefield state
   * @param speeds - Movement speeds for the token
   * @param options - Movement options
   * @returns Movement validation result
   */
  static validateMovement(
    moverId: string,
    path: MovementPath,
    battlefield: Battlefield,
    speeds: SpeedModes,
    options: MovementOptions = {}
  ): MovementResult {
    const gridSize = battlefield.gridSize || 5;
    const mover = battlefield.tokens.find(t => t.id === moverId);

    if (!mover) {
      return {
        ok: false,
        spent: 0,
        remaining: 0,
        path: [],
        triggers: { opportunityAttacks: [] },
        errors: [`Token ${moverId} not found on battlefield`]
      };
    }

    // @date_of_change: 2025-09-21 - Add condition-based movement validation
    // Check movement restrictions from conditions
    if (mover.activeConditionEffects && path.points.length > 1) {
      const targetPosition = path.points[path.points.length - 1];
      const conditionValidation = ConditionHandler.validateMovement(
        { id: moverId, x: mover.position.x, y: mover.position.y },
        targetPosition,
        mover.activeConditionEffects,
        battlefield
      );

      if (!conditionValidation.allowed) {
        return {
          ok: false,
          spent: 0,
          remaining: 0,
          path: [],
          triggers: { opportunityAttacks: [] },
          errors: conditionValidation.reasons
        };
      }

      // Apply speed modifications from conditions
      if (conditionValidation.modifiedSpeed !== undefined) {
        speeds = {
          ...speeds,
          walk: speeds.walk * conditionValidation.modifiedSpeed
        };
      }
    }

    // Determine movement speed to use
    const speed = this.getApplicableSpeed(speeds, options);
    if (speed === 0) {
      return {
        ok: false,
        spent: 0,
        remaining: 0,
        path: [],
        triggers: { opportunityAttacks: [] },
        errors: ['No applicable movement speed available']
      };
    }

    const result: MovementResult = {
      ok: true,
      spent: 0,
      remaining: speed,
      path: [],
      triggers: { opportunityAttacks: [] },
      warnings: []
    };

    // Validate each segment of the path
    for (let i = 0; i < path.points.length - 1; i++) {
      const from = path.points[i];
      const to = path.points[i + 1];

      const segmentResult = this.validateSegment(
        from,
        to,
        battlefield,
        moverId,
        options,
        gridSize
      );

      result.spent += segmentResult.cost;
      result.path.push(from);

      // Check for opportunity attacks
      // Disengage is a persisted turn flag on the mover. It suppresses OA
      // discovery for normal movement without turning that movement into
      // forced movement (which has different semantics).
      // date_of_change: 2026-07-18
      if (!options.forcedMovement && mover.combatFlags?.disengagedThisTurn !== true) {
        const oaTokens = this.checkOpportunityAttacks(from, to, battlefield, moverId);
        result.triggers.opportunityAttacks.push(...oaTokens);
      }

      // Ignore action economy never changes path legality, conditions, terrain,
      // or opportunity attacks; it only removes the per-turn distance ceiling.
      // rule_reference: BattleRoom GM "Ignore action economy" capability contract.
      // date_of_change: 2026-09-06
      if (!options.ignoreActionEconomy && result.spent > speed) {
        result.ok = false;
        result.errors = result.errors || [];
        result.errors.push(`Movement cost (${result.spent} ft) exceeds available speed (${speed} ft)`);
        break;
      }

      // Add any segment warnings
      if (segmentResult.warnings) {
        result.warnings!.push(...segmentResult.warnings);
      }
    }

    // Add final point if movement is valid
    if (result.ok && path.points.length > 0) {
      result.path.push(path.points[path.points.length - 1]);
    }

    result.remaining = Math.max(0, speed - result.spent);

    // Handle grappling constraints
    if (options.grappling) {
      const grappleResult = this.validateGrappleMovement(
        moverId,
        options.grappling,
        result,
        battlefield
      );

      if (!grappleResult.ok) {
        result.ok = false;
        result.errors = result.errors || [];
        result.errors.push(...grappleResult.errors);
      }
    }

    return result;
  }

  /**
   * Validates a single movement segment.
   */
  private static validateSegment(
    from: Point,
    to: Point,
    battlefield: Battlefield,
    moverId: string,
    options: MovementOptions,
    gridSize: number
  ): { cost: number; warnings?: string[] } {
    const distance = this.calculateDistance(from, to, gridSize);
    let cost = distance;
    const warnings: string[] = [];

    // Apply terrain costs
    const terrainKey = `${to.x},${to.y}`;
    const terrain = battlefield.terrain?.get(terrainKey);

    if (terrain) {
      switch (terrain.type) {
        case 'difficult':
          cost *= 2;
          warnings.push(`Difficult terrain (${terrain.cost}x cost)`);
          break;
        case 'hazardous':
          cost *= terrain.cost;
          warnings.push(`Hazardous terrain (${terrain.cost}x cost)`);
          break;
        case 'impassable':
          cost = Infinity;
          warnings.push('Impassable terrain');
          break;
      }
    }

    // Apply movement type modifiers
    if (options.climbing && !options.flying) {
      cost *= 2;
      warnings.push('Climbing (2x cost)');
    }

    if (options.swimming && !options.flying) {
      cost *= 2;
      warnings.push('Swimming (2x cost)');
    }

    // Check for squeezing (token in smaller space)
    const isSqueezing = this.checkSqueezing(to, battlefield, moverId);
    if (isSqueezing) {
      cost *= 2;
      warnings.push('Squeezing (2x cost)');
    }

    return { cost, warnings: warnings.length > 0 ? warnings : undefined };
  }

  /**
   * Checks for opportunity attacks along a movement segment.
   */
  private static checkOpportunityAttacks(
    from: Point,
    to: Point,
    battlefield: Battlefield,
    moverId: string
  ): string[] {
    const oaTokens: string[] = [];

    for (const token of battlefield.tokens) {
      if (token.id === moverId) continue;

      // Check if token can make opportunity attacks
      if (token.conditions?.includes('incapacitated') ||
          token.conditions?.includes('stunned') ||
          token.conditions?.includes('unconscious')) {
        continue;
      }

      const reach = token.reach || 5;

      // Check if we're leaving their reach
      const wasInReach = this.calculateDistance(from, token.position, 5) <= reach;
      const stillInReach = this.calculateDistance(to, token.position, 5) <= reach;

      if (wasInReach && !stillInReach) {
        oaTokens.push(token.id);
      }
    }

    return oaTokens;
  }

  /**
   * Validates grapple movement constraints.
   */
  private static validateGrappleMovement(
    grapplerX: string,
    grappledId: string,
    movementResult: MovementResult,
    battlefield: Battlefield
  ): { ok: boolean; errors: string[] } {
    const grappled = battlefield.tokens.find(t => t.id === grappledId);
    if (!grappled) {
      return { ok: false, errors: ['Grappled target not found'] };
    }

    // Movement is halved when dragging
    const halfSpeed = movementResult.spent / 2;
    if (movementResult.spent > halfSpeed) {
      return {
        ok: false,
        errors: [`Grappling reduces movement to half speed (${halfSpeed} ft available)`]
      };
    }

    // Grappled creature must stay adjacent
    if (movementResult.path.length > 0) {
      const finalPosition = movementResult.path[movementResult.path.length - 1];
      const distance = this.calculateDistance(finalPosition, grappled.position, 5);

      if (distance > 5) {
        return {
          ok: false,
          errors: ['Grappled creature must remain adjacent (within 5 feet)']
        };
      }
    }

    return { ok: true, errors: [] };
  }

  /**
   * Gets the applicable movement speed based on options.
   */
  private static getApplicableSpeed(speeds: SpeedModes, options: MovementOptions): number {
    if (options.flying && speeds.fly) {
      return speeds.fly;
    }

    if (options.swimming && speeds.swim) {
      return speeds.swim;
    }

    if (options.climbing && speeds.climb) {
      return speeds.climb;
    }

    return speeds.walk;
  }

  /**
   * Calculates distance between two points using D&D 5e rules.
   * Uses the same calculation method as RangeCalculator for consistency.
   * @date_of_change: 2025-09-21 - Fixed movement calculation to match RangeCalculator approach
   */
  private static calculateDistance(p1: Point, p2: Point, gridSize: number): number {
    const dx = Math.abs(p2.x - p1.x);
    const dy = Math.abs(p2.y - p1.y);

    // D&D 5e uses "every other diagonal counts as 10 feet" rule
    const straight = Math.min(dx, dy);
    const remaining = Math.max(dx, dy) - straight;

    // Standard 5e rule: First diagonal = 5ft, second = 10ft, alternating
    let diagonalFeet = 0;
    for (let i = 0; i < straight; i++) {
      diagonalFeet += (i % 2 === 0) ? 5 : 10;
    }

    const totalFeet = diagonalFeet + (remaining * gridSize);
    return totalFeet;
  }

  /**
   * Checks if a token is squeezing in the destination square.
   */
  private static checkSqueezing(position: Point, battlefield: Battlefield, moverId: string): boolean {
    // This is a simplified check - in practice, would need map data about square sizes
    // For now, assume no squeezing
    return false;
  }

  /**
   * Pre-validates movement before execution to catch issues early.
   * @param moverId - Token attempting to move
   * @param targetPosition - Desired destination
   * @param battlefield - Current battlefield state
   * @param speeds - Movement speeds
   * @param options - Movement options
   * @returns Quick validation result
   */
  static preValidateMovement(
    moverId: string,
    targetPosition: Point,
    battlefield: Battlefield,
    speeds: SpeedModes,
    options: MovementOptions = {}
  ): { ok: boolean; reason?: string } {
    const mover = battlefield.tokens.find(t => t.id === moverId);
    if (!mover) {
      return { ok: false, reason: 'Token not found' };
    }

    // Check if movement is possible at all
    const speed = this.getApplicableSpeed(speeds, options);
    if (speed === 0) {
      return { ok: false, reason: 'No movement speed available' };
    }

    // Check straight-line distance as minimum requirement
    const distance = this.calculateDistance(mover.position, targetPosition, battlefield.gridSize || 5);
    if (!options.ignoreActionEconomy && distance > speed) {
      return { ok: false, reason: `Distance (${distance} ft) exceeds speed (${speed} ft)` };
    }

    // Check for impassable terrain at destination
    const terrainKey = `${targetPosition.x},${targetPosition.y}`;
    const terrain = battlefield.terrain?.get(terrainKey);
    if (terrain?.type === 'impassable') {
      return { ok: false, reason: 'Destination is impassable terrain' };
    }

    return { ok: true };
  }

  /**
   * Calculates the optimal path between two points, considering terrain.
   * @param from - Starting position
   * @param to - Target position
   * @param battlefield - Battlefield state
   * @returns Optimal movement path
   */
  static calculateOptimalPath(
    from: Point,
    to: Point,
    battlefield: Battlefield
  ): MovementPath {
    // Simple straight-line path for now
    // In practice, would implement A* pathfinding with terrain costs
    const points = [from, to];
    const totalDistance = this.calculateDistance(from, to, battlefield.gridSize || 5);

    return { points, totalDistance };
  }
}
